import { Routes } from '@all-hands-hq/plex-graphql-schema';
import * as Sentry from '@sentry/react';
import React, { Fragment, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import {
  GetLoggedInUserQuery,
  GetUserNotificationsSubscriptionSubscription,
  GetUserNotificationsSubscriptionSubscriptionVariables,
  useGetUserNotificationsQuery,
} from '../../graphql/gen/types';
import { GET_USER_NOTIFICATIONS_SUBSCRIPTION } from '../../graphql/subscriptions/GetNotifications';
import { DetailsDrawer } from './details/DetailsDrawer';
import { useEffectOnce } from './details/useEffectOnce';
import { DialogContainer } from './dialog/DialogContainer';
import { PlexFeed } from './feed/PlexFeed';
import { GenericErrorPage } from './GenericErrorPage';
import { Onboarding } from './onboarding/Onboarding';
import { useResetOnboardingOnCommand } from './onboarding/useResetOnboardingOnCommand';
import { PaywallAlert } from './PaywallAlert';
import { SubscribeSnackbar } from './plexes/SubscribeSnackbar';
import { SettingsContainer } from './settings/SettingsContainer';

type Props = {
  userProfile: NonNullable<GetLoggedInUserQuery['currentUser']>;
};

export const LoggedInMain = (props: Props): React.ReactElement => {
  const { userProfile } = props;
  const history = useHistory();
  useResetOnboardingOnCommand();
  const { subscribeToMore } = useGetUserNotificationsQuery({ variables: {} });

  useEffect(() => {
    const unsubscribe = subscribeToMore<
      GetUserNotificationsSubscriptionSubscription,
      GetUserNotificationsSubscriptionSubscriptionVariables
    >({
      document: GET_USER_NOTIFICATIONS_SUBSCRIPTION,
      variables: {},
    });

    return unsubscribe;
  }, [subscribeToMore]);

  useEffectOnce(() => {
    Sentry.configureScope((scope) => {
      scope.setUser({ email: userProfile.email });
    });
  });

  return (
    <Fragment>
      <Switch>
        <Route path={[Routes.PLEX_FEED_PREFIX_PATH, Routes.PLEX_POST_PERMALINK_PATH, Routes.PLEX_PERMALINK_PATH]}>
          <PlexFeed userProfile={userProfile} />
        </Route>
        <Route path={Routes.SETTINGS_PATH}>
          <SettingsContainer userProfile={userProfile} />
        </Route>

        <Route exact path={Routes.NOT_FOUND_PATH}>
          <GenericErrorPage showNavbar message="The page you are looking for could not be found" />
        </Route>

        <Route exact path={Routes.ONBOARDING}>
          <Onboarding onClose={(): void => history.push(Routes.HOME_PATH)} userProfile={userProfile} />
        </Route>

        <Redirect to={Routes.PLEX_FEED_PATH} />
      </Switch>

      <DetailsDrawer />
      <DialogContainer />
      <SubscribeSnackbar />
      <PaywallAlert />
    </Fragment>
  );
};
